import React, { useState, useEffect } from 'react';
import api from '../api';
import { UploadCloud } from 'lucide-react';

const InputField = ({ label, name, value, onChange, error, ...props }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
      {label}
    </label>
    <input
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      {...props}
      className={`mt-1 block w-full px-3 py-2 bg-white dark:bg-neutral-700 border 
        ${error ? 'border-red-500' : 'border-neutral-300 dark:border-neutral-600'} 
        rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
    />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);


const FileInputField = ({ label, name, onChange, disabled, multiple = false, selectedFiles = [] }) => {
  const handleFileChange = (e) => {
    console.log(`File change for ${name}:`, e.target.files.length, 'files');
    onChange(e);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
        {label} {multiple && '(Select up to 2 files - Front & Back)'}
      </label>
      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-600 border-dashed rounded-md">
        <div className="space-y-1 text-center">
          <UploadCloud className="mx-auto h-12 w-12 text-neutral-400" />
          <div className="flex text-sm text-neutral-600 dark:text-neutral-400">
            <label
              htmlFor={name}
              className="relative cursor-pointer bg-white dark:bg-neutral-800 rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
            >
              <span>Choose {multiple ? 'up to 2 files' : 'file'}</span>
              <input
                id={name}
                name={name}
                type="file"
                className="sr-only"
                onChange={handleFileChange}
                disabled={disabled}
                multiple={multiple}
                accept="image/*,image/jpeg,image/png,image/gif"
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            PNG, JPG, GIF up to 10MB each {multiple && '(Select multiple files at once)'}
          </p>
          {selectedFiles.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-green-600 mb-1">
                {selectedFiles.length} file(s) selected
              </div>
              <div className="text-xs text-gray-500">
                {selectedFiles.map((file, index) => (
                  <div key={index}>
                    {file.name} ({Math.round(file.size / 1024)}KB)
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const NewPartnerForm = ({ onPartnerAdded }) => {
  const [formData, setFormData] = useState({
    partner_name: '',
    partner_number: '',
    // vehicle_no: '',
    security_deposit: '25000',
    // deposit_received: '',
    collection_method: 'Cash',
  });
  const [files, setFiles] = useState({
    pan_card: [],
    aadhaar_card: [],

    photo: [],
    dl: [],
  });
  const [unassignedVehicles, setUnassignedVehicles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [vehiclesResponse] = await Promise.all([
          api.get('/vehicles/unassigned'),
        ]);
        setUnassignedVehicles(vehiclesResponse.data || []);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFieldErrors((prev) => ({ ...prev, [name]: '' })); // clear field error
  };

  const validateFields = () => {
    let errors = {};

    // Partner Name Validation
    const name = formData.partner_name.trim();
    if (!name) {
      errors.partner_name = 'Partner name is required';
    } else if (!/^[A-Za-z\s]+$/.test(name)) {
      errors.partner_name = 'Name should only contain alphabets and spaces';
    } else if (/^\s|\s$/.test(formData.partner_name)) {
      errors.partner_name = 'No leading or trailing spaces allowed';
    }

    // Partner Number Validation
    const number = formData.partner_number.trim();
    if (!number) {
      errors.partner_number = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(number)) {
      errors.partner_number = 'Phone number must start with 6-9 and be exactly 10 digits';
    } else if (/^\s|\s$/.test(formData.partner_number)) {
      errors.partner_number = 'No leading or trailing spaces allowed';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    console.log('handleFileChange called:', {
      fieldName: name,
      filesSelected: selectedFiles.length,
      fileNames: Array.from(selectedFiles).map(f => f.name)
    });

    // Convert FileList to Array and limit based on field requirements
    const fileArray = Array.from(selectedFiles);

    // Fields that support multiple files (front & back)
    const multipleFileFields = ['pan_card', 'aadhaar_card', 'dl'];

    if (multipleFileFields.includes(name)) {
      // Limit to 2 files for front & back
      const limitedFiles = fileArray.slice(0, 2);
      console.log(`Setting ${limitedFiles.length} files for ${name}:`, limitedFiles.map(f => f.name));
      setFiles(prevFiles => {
        const newFiles = { ...prevFiles, [name]: limitedFiles };
        console.log('Updated files state:', newFiles);
        return newFiles;
      });
    } else {
      // Single file for photo
      const singleFile = fileArray.slice(0, 1);
      console.log(`Setting ${singleFile.length} file for ${name}:`, singleFile.map(f => f.name));
      setFiles(prevFiles => {
        const newFiles = { ...prevFiles, [name]: singleFile };
        console.log('Updated files state:', newFiles);
        return newFiles;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validateFields()) {
      return; // stop submit if invalid
    }
    setIsSubmitting(true);
    setError(null);

    console.log('--- Form Submission Started ---');
    console.log('Form data:', formData);
    console.log('Files data:', files);

    const submissionData = new FormData();

    if (!validateFields()) {
      return; // stop submit if invalid
    }
    // Add form fields
    for (const key in formData) {
      submissionData.append(key, formData[key]);
      console.log(`Added form field: ${key} = ${formData[key]}`);
    }

    // Handle file uploads - append each file in the array
    let totalFiles = 0;
    for (const key in files) {
      if (files[key] && files[key].length > 0) {
        console.log(`Adding ${files[key].length} file(s) for ${key}:`);
        files[key].forEach((file, index) => {
          submissionData.append(key, file);
          console.log(`  - File ${index + 1}: ${file.name} (${file.size} bytes)`);
          totalFiles++;
        });
      }
    }

    console.log(`Total files to upload: ${totalFiles}`);

    // Log FormData contents (for debugging)
    console.log('FormData entries:');
    for (let [key, value] of submissionData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
      } else {
        console.log(`  ${key}: ${value}`);
      }
    }

    try {
      console.log('Sending request to /partners...');
      const response = await api.post('/partners', submissionData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Success response:', response.data);
      alert('Partner onboarded successfully!');
      if (onPartnerAdded) {
        onPartnerAdded();
      }
    } catch (err) {
      console.error('--- Form Submission Error ---');
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 p-6 bg-neutral-50 dark:bg-neutral-900 h-full overflow-y-auto"
    >
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">
          Onboard New Partner
        </h2>
        <p className="text-sm text-neutral-500">
          Fill in the details below to add a new partner to the system.
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-4">
        <InputField
          label="Partner Name"
          name="partner_name"
          value={formData.partner_name}
          onChange={handleChange}
          required
          disabled={isSubmitting}
          error={fieldErrors.partner_name}
        />

        <InputField
          label="Partner Phone Number"
          name="partner_number"
          value={formData.partner_number}
          onChange={handleChange}
          required
          disabled={isSubmitting}
          error={fieldErrors.partner_number}
        />


        <FileInputField
          label="PAN Card"
          name="pan_card"
          onChange={handleFileChange}
          disabled={isSubmitting}
          multiple={true}
          selectedFiles={files.pan_card}
        />
        <FileInputField
          label="Aadhaar Card"
          name="aadhaar_card"
          onChange={handleFileChange}
          disabled={isSubmitting}
          multiple={true}
          selectedFiles={files.aadhaar_card}
        />
        <FileInputField
          label="Photo"
          name="photo"
          onChange={handleFileChange}
          disabled={isSubmitting}
          selectedFiles={files.photo}
        />
        <FileInputField
          label="Driving License"
          name="dl"
          onChange={handleFileChange}
          disabled={isSubmitting}
          multiple={true}
          selectedFiles={files.dl}
        />

        {/* <div>
          <label htmlFor="vehicle_no" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">Assign Vehicle</label>
          <select id="vehicle_no" name="vehicle_no" value={formData.vehicle_no} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md" required disabled={isSubmitting}>
            <option value="">Select a Vehicle</option>
            {unassignedVehicles.map((v) => (
              <option key={v.id} value={v.vehicle_no}>{v.vehicle_no}</option>
            ))}
          </select>
        </div> */}

        {/* <InputField label="Security Deposit" name="security_deposit" value={formData.security_deposit} onChange={handleChange} disabled /> */}
        {/* <InputField label="Deposit Received" name="deposit_received" type="number" value={formData.deposit_received} onChange={handleChange} disabled={isSubmitting} /> */}
      </div>

      <div className="pt-5">
        <button type="submit" disabled={isSubmitting} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50">
          {isSubmitting ? 'Onboarding...' : 'Onboard Partner'}
        </button>
      </div>
    </form>
  );
};

export default NewPartnerForm;
